<template>
  <div class="changelog">
    <themed-table
      :columns="tableColumns"
      :api-endpoint="apiEndpoint"
      :pagination="true"
      :per-page-options="[10, 25, 50]"
      :searchable="false"
      :sortable="true"
      :hoverable="true"
      :bordered="bordered"
      aria-label="Changelog table"
    />
  </div>
</template>

<script>
import moment from 'moment';
import ThemedTable from '../ThemedTable.vue';

export default {
  name: 'ThemedChangelog',
  
  components: {
    ThemedTable
  },
  
  props: {
    // Resource type (e.g., 'project', 'site', 'lease', 'building')
    resourceType: {
      type: String,
      required: true,
      validator: (value) => ['project', 'site', 'lease', 'building', 'proposal'].includes(value)
    },
    
    // Resource ID
    resourceId: {
      type: [Number, String],
      required: true
    },
    
    // Optional: Custom API endpoint (overrides default)
    customApiEndpoint: {
      type: String,
      default: null
    },
    
    // Table styling
    bordered: {
      type: Boolean,
      default: false
    }
  },
  
  computed: {
    /**
     * Build API endpoint based on resource type
     */
    apiEndpoint() {
      // Use custom endpoint if provided
      if (this.customApiEndpoint) {
        return this.customApiEndpoint;
      }
      
      // Build default endpoint based on resource type
      const endpoints = {
        project: `/api/projects/${this.resourceId}/audits`,
        site: `/api/site/${this.resourceId}/audits`,
        lease: `/api/leases/${this.resourceId}/audits`,
        building: `/api/buildings/${this.resourceId}/audits`,
        proposal: `/api/proposals/${this.resourceId}/audits`
      };
      
      return endpoints[this.resourceType];
    },
    
    /**
     * Table columns configuration
     */
    tableColumns() {
      return [
        {
          field: 'created_at',
          label: 'Date',
          type: 'text',
          width: '180px',
          sortable: true,
          align: 'center',
          formatter: (value) => {
            if (!value) return '-';
            return moment(value).format('MMM D, YYYY h:mm A');
          }
        },
        {
          field: 'user',
          label: 'Changed By',
          type: 'text',
          width: '250px',
          sortable: false,
          formatter: (value, row) => {
            const userName = row.user?.name || 'Unknown User';
            const ipAddress = row.ip_address || '-';
            
            return `
              <div class="changelog__user">
                <p class="changelog__user-name">${userName}</p>
                <p class="changelog__user-ip">${ipAddress}</p>
              </div>
            `;
          }
        },
        {
          field: 'changes',
          label: 'Changes',
          type: 'text',
          sortable: false,
          formatter: (value, row) => {
            let html = '<ul class="changelog__changes-list">';
            
            // Handle creation event
            if (row.event === 'created') {
              html += '<li class="changelog__change-item"><span class="changelog__change-event">Created</span></li>';
            }
            
            // Handle field changes
            if (row.old_values && typeof row.old_values === 'object') {
              for (const [field, oldValue] of Object.entries(row.old_values)) {
                const newValue = row.new_values?.[field];
                const fieldName = this.formatFieldName(field);
                
                html += `
                  <li class="changelog__change-item">
                    <strong class="changelog__change-field">${fieldName}</strong>
                    changed from
                    <strong class="changelog__change-value">${oldValue || '-'}</strong>
                    to
                    <strong class="changelog__change-value">${newValue || '-'}</strong>
                  </li>
                `;
              }
            }
            
            html += '</ul>';
            return html;
          }
        }
      ];
    }
  },
  
  methods: {
    /**
     * Format field name for display (convert snake_case to Title Case)
     */
    formatFieldName(field) {
      if (!field) return '';
      
      return field
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
  }
};
</script>
